import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Select from '@/components/base/Select';
import Alert from '@/components/base/Alert';
import StatusBadge from '@/components/base/StatusBadge';
import { adminApi } from '@/lib/adminApi';
import type { AdminBank, AdminTransfer } from '@/lib/adminApi';
import { formatDate, formatMoney } from '@/lib/format';
import { downloadTransferPdf, openTransferPdf } from '@/lib/transferPdf';
import type { TransferPdfData } from '@/lib/transferPdf';
import {
  AdminCard,
  EmptyBlock,
  ErrorBlock,
  LoadingBlock,
  Modal,
  StatCard,
  TableCell,
  TableHeaderCell,
} from './ui';

const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'CAD', 'XPF'];
/** Doit rester aligné avec le plafond du Backend. */
const MAX_TRANSFER_AMOUNT = 999_999_999;
const MAIL_LANGS = ['fr', 'en', 'it', 'de', 'pt', 'es'];
const MAIL_LANG_LABELS: Record<string, string> = {
  fr: 'Français',
  en: 'English',
  it: 'Italiano',
  de: 'Deutsch',
  pt: 'Português',
  es: 'Español',
};
const FILTERS = ['', 'pending', 'verified', 'executed', 'cancelled'] as const;

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  iban: '',
  currency: 'EUR',
  amount: '',
  bankId: '',
  note: '',
  lang: 'fr',
};

function EmailStateBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const tones: Record<string, string> = {
    sent: 'bg-primary-100 text-primary-800 border-primary-200',
    failed: 'bg-accent-100 text-accent-900 border-accent-200',
    queued: 'bg-secondary-100 text-secondary-800 border-secondary-200',
    not_configured: 'bg-background-200 text-foreground-700 border-background-300',
  };
  const icons: Record<string, string> = {
    sent: 'ri-mail-check-line',
    failed: 'ri-mail-close-line',
    queued: 'ri-mail-line',
    not_configured: 'ri-mail-forbid-line',
  };
  const tone = tones[status] ?? tones.not_configured;
  const icon = icons[status] ?? 'ri-mail-line';

  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold font-label ${tone}`}
    >
      <span className="flex items-center justify-center w-3 h-3">
        <i className={`${icon} text-[11px]`} aria-hidden="true" />
      </span>
      {t(`admin.emailState.${status}`)}
    </span>
  );
}

function LinkBadge({ active }: { active: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold font-label ${
        active
          ? 'bg-primary-50 text-primary-800 border-primary-200'
          : 'bg-background-200 text-foreground-600 border-background-300'
      }`}
    >
      <span className="flex items-center justify-center w-3 h-3">
        <i className={active ? 'ri-link text-[11px]' : 'ri-link-unlink text-[11px]'} aria-hidden="true" />
      </span>
      {active ? t('admin.transfers.linkActive') : t('admin.transfers.linkDisabled')}
    </span>
  );
}

function AuthStateBadge({ transfer }: { transfer: AdminTransfer }) {
  const { t } = useTranslation();
  const key = ['executed', 'cancelled'].includes(transfer.status)
    ? 'closed'
    : transfer.authLocked
      ? 'locked'
      : transfer.stage === 'authenticated'
        ? 'authenticated'
        : 'created';

  const tones: Record<string, string> = {
    authenticated: 'bg-primary-600 text-background-50 border-primary-700',
    created: 'bg-background-200 text-foreground-700 border-background-300',
    locked: 'bg-accent-100 text-accent-900 border-accent-200',
    closed: 'bg-background-200 text-foreground-600 border-background-300',
  };
  const icons: Record<string, string> = {
    authenticated: 'ri-shield-check-line',
    created: 'ri-mail-send-line',
    locked: 'ri-lock-line',
    closed: 'ri-checkbox-circle-line',
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold font-label ${tones[key]}`}
    >
      <span className="flex items-center justify-center w-3 h-3">
        <i className={`${icons[key]} text-[11px]`} aria-hidden="true" />
      </span>
      {t(`admin.authState.${key}`)}
    </span>
  );
}

export default function TransfersTab({ token }: { token: string }) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<AdminTransfer[]>([]);
  const [banks, setBanks] = useState<AdminBank[]>([]);
  const [filter, setFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const [composerOpen, setComposerOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState('');
  const [formNotice, setFormNotice] = useState('');

  const [selected, setSelected] = useState<AdminTransfer | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [detailMessage, setDetailMessage] = useState('');
  const [detailError, setDetailError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await adminApi.transfersList(token, filter);
      if (!res.ok) {
        setFailed(true);
        return;
      }
      setRows((res.transfers ?? []) as AdminTransfer[]);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let active = true;
    adminApi.banksList(token).then((res) => {
      if (!active || !res.ok) return;
      setBanks(((res.banks ?? []) as AdminBank[]).filter((bank) => bank.active));
    });
    return () => {
      active = false;
    };
  }, [token]);

  const stats = useMemo(() => {
    const pending = rows.filter((row) => row.status === 'pending').length;
    const verified = rows.filter((row) => row.status === 'verified').length;
    const executed = rows.filter((row) => row.status === 'executed').length;
    const disabled = rows.filter((row) => !row.linkActive).length;
    return { total: rows.length, pending, verified, executed, disabled };
  }, [rows]);

  const bankOptions = banks.map((bank) => ({ value: String(bank.id), label: bank.name }));

  const activationLink = (row: AdminTransfer) => {
    const base = String(__BASE_PATH__ ?? '').replace(/\/+$/, '');
    return `${window.location.origin}${base}/transfer?t=${row.activationToken ?? ''}`;
  };

  const pdfData = useCallback(
    (row: AdminTransfer): TransferPdfData => ({
      reference: row.reference,
      beneficiaryName: row.beneficiaryName,
      email: row.email,
      bankName: row.bankName,
      ibanMasked: row.ibanMasked,
      amount: row.amount,
      currency: row.currency,
      statusLabel: t(`status.${row.status}`),
      createdAt: row.createdAt,
      verifiedAt: row.verifiedAt,
      link: activationLink(row),
      linkActive: row.linkActive,
      note: row.note,
      clientName: row.clientName,
      clientLogin: row.clientLogin,
      language: i18n.language,
    }),
    [t, i18n.language],
  );

  const copyActivationLink = async (row: AdminTransfer) => {
    try {
      await navigator.clipboard.writeText(activationLink(row));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const openComposer = () => {
    const current = String(i18n.language ?? 'fr').slice(0, 2).toLowerCase();
    setForm({ ...emptyForm, lang: MAIL_LANGS.includes(current) ? current : 'fr' });
    setFormError('');
    setFormNotice('');
    setComposerOpen(true);
  };

  const submit = async () => {
    if (creating) return;
    setFormError('');
    setFormNotice('');

    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = form.email.trim();
    const iban = form.iban.replace(/\s+/g, '').toUpperCase();
    const amount = Number(form.amount.replace(',', '.'));

    if (!firstName || !lastName || !email || !iban || !form.bankId || !form.amount) {
      setFormError(t('admin.transfers.missing'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setFormError(t('admin.transfers.invalidEmail'));
      return;
    }
    if (iban.length < 8) {
      setFormError(t('admin.transfers.invalidIban'));
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setFormError(t('admin.transfers.invalidAmount'));
      return;
    }
    if (amount > MAX_TRANSFER_AMOUNT) {
      setFormError(
        t('admin.transfers.amountTooHigh', {
          max: formatMoney(MAX_TRANSFER_AMOUNT, form.currency, i18n.language),
        }),
      );
      return;
    }

    setCreating(true);
    const res = await adminApi.transferCreate(token, {
      firstName,
      lastName,
      email,
      iban,
      currency: form.currency,
      amount,
      bankId: Number(form.bankId),
      note: form.note.trim(),
      lang: form.lang,
    });
    setCreating(false);

    if (!res.ok) {
      setFormError(
        res.code === 'INVALID_BANK' || res.code === 'BANK_INACTIVE'
          ? t('admin.transfers.invalidBank')
          : res.code === 'INVALID_IBAN'
            ? t('admin.transfers.invalidIban')
            : res.code === 'INVALID_AMOUNT'
              ? t('admin.transfers.invalidAmount')
              : res.code === 'AMOUNT_TOO_HIGH'
                ? t('admin.transfers.amountTooHigh', {
                    max: formatMoney(MAX_TRANSFER_AMOUNT, form.currency, i18n.language),
                  })
                : t('errors.server'),
      );
      return;
    }

    const transfer = res.transfer as AdminTransfer;
    const emailStatus = String(res.emailStatus ?? '');
    const baseNotice =
      emailStatus === 'sent'
        ? t('admin.transfers.createdSent', { reference: transfer.reference })
        : t('admin.transfers.createdNoMail', { reference: transfer.reference });
    setFormNotice(
      res.clientCreated
        ? `${baseNotice} ${t('admin.transfers.clientCreated', { login: String(res.clientLogin ?? '—') })}`
        : baseNotice,
    );
    setForm({ ...emptyForm });
    load();
  };

  const act = async (action: 'resend' | 'executed' | 'cancelled' | 'link-on' | 'link-off' | 'credentials') => {
    if (!selected || busy) return;
    setBusy(true);
    setDetailError('');
    setDetailMessage('');
    const res =
      action === 'resend'
        ? await adminApi.transferResend(token, selected.id)
        : action === 'credentials'
          ? await adminApi.transferSendCredentials(token, selected.id)
          : action === 'link-on' || action === 'link-off'
            ? await adminApi.transferLink(token, selected.id, action === 'link-on')
            : await adminApi.transferUpdate(token, selected.id, action);
    setBusy(false);

    if (!res.ok) {
      setDetailError(
        res.code === 'ALREADY_EXECUTED'
          ? t('admin.transfers.alreadyExecuted')
          : res.code === 'NOT_VERIFIED'
            ? t('admin.transfers.notVerified')
            : res.code === 'NO_CLIENT'
              ? t('admin.transfers.noClient')
              : t('errors.server'),
      );
      return;
    }

    if (action === 'resend') {
      const emailStatus = String(res.emailStatus ?? '');
      setDetailMessage(
        emailStatus === 'sent' ? t('admin.transfers.resent') : t('admin.transfers.resendFailed'),
      );
      if (res.transfer) setSelected(res.transfer as AdminTransfer);
    } else if (action === 'credentials') {
      setDetailMessage(
        res.emailStatus === 'sent'
          ? t('admin.transfers.credentialsSent')
          : t('admin.transfers.credentialsNotSent'),
      );
    } else if (action === 'link-on' || action === 'link-off') {
      if (res.transfer) setSelected(res.transfer as AdminTransfer);
      setDetailMessage(action === 'link-on' ? t('admin.transfers.linkEnabled') : t('admin.transfers.linkDisableDone'));
    } else {
      if (res.transfer) setSelected(res.transfer as AdminTransfer);
      setDetailMessage(t('admin.transfers.updated'));
    }
    load();
  };

  const filterLabel = (value: string) => (value ? t(`status.${value}`) : t('admin.common.all'));

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
        <StatCard label={t('admin.transfers.statTotal')} value={String(stats.total)} icon="ri-exchange-dollar-line" />
        <StatCard
          label={t('admin.transfers.statPending')}
          value={String(stats.pending)}
          icon="ri-time-line"
          tone="accent"
        />
        <StatCard
          label={t('admin.transfers.statVerified')}
          value={String(stats.verified)}
          icon="ri-shield-check-line"
          tone="primary"
        />
        <StatCard
          label={t('admin.transfers.statLinks')}
          value={String(stats.disabled)}
          icon="ri-link-unlink"
        />
      </div>

      <AdminCard
        title={t('admin.transfers.title')}
        subtitle={t('admin.transfers.subtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center p-1 rounded-full border border-background-300 bg-background-100">
              {FILTERS.map((value) => (
                <button
                  key={value || 'all'}
                  type="button"
                  onClick={() => setFilter(value)}
                  className={`h-7 px-3 rounded-full text-[11.5px] font-semibold font-label whitespace-nowrap transition-colors cursor-pointer ${
                    filter === value
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  {filterLabel(value)}
                </button>
              ))}
            </div>
            <Button size="sm" onClick={openComposer} icon={<i className="ri-add-line" aria-hidden="true" />}>
              {t('admin.transfers.new')}
            </Button>
          </div>
        }
        padded={false}
      >
        {loading ? (
          <LoadingBlock />
        ) : failed ? (
          <ErrorBlock onRetry={load} />
        ) : rows.length === 0 ? (
          <EmptyBlock label={t('admin.transfers.empty')} icon="ri-exchange-dollar-line" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1040px]">
              <thead className="bg-background-100">
                <tr>
                  <TableHeaderCell>{t('admin.transfers.ref')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.transfers.beneficiary')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.transfers.bank')}</TableHeaderCell>
                  <TableHeaderCell align="right">{t('admin.transfers.amount')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.transfers.status')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.transfers.activation')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.transfers.linkColumn')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.transfers.emailStatus')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.transfers.date')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.common.actions')}</TableHeaderCell>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-background-100/60 transition-colors">
                    <TableCell>
                      <span className="font-semibold text-foreground-950 font-label whitespace-nowrap">
                        {row.reference}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="block text-[13px] text-foreground-900">{row.beneficiaryName}</span>
                      <span className="block text-[11.5px] text-foreground-400 break-all">{row.email}</span>
                    </TableCell>
                    <TableCell>{row.bankName}</TableCell>
                    <TableCell align="right">
                      <span className="font-semibold text-foreground-950">
                        {formatMoney(row.amount, row.currency, i18n.language)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={row.status} />
                    </TableCell>
                    <TableCell>
                      <AuthStateBadge transfer={row} />
                    </TableCell>
                    <TableCell>
                      <LinkBadge active={row.linkActive} />
                    </TableCell>
                    <TableCell>
                      <EmailStateBadge status={row.emailStatus} />
                    </TableCell>
                    <TableCell>
                      <span className="text-foreground-500 whitespace-nowrap">
                        {formatDate(row.createdAt, i18n.language, true)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setSelected(row);
                            setDetailMessage('');
                            setDetailError('');
                          }}
                          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-background-300 text-[11.5px] font-semibold text-foreground-700 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <span className="flex items-center justify-center w-3.5 h-3.5">
                            <i className="ri-eye-line text-[13px]" aria-hidden="true" />
                          </span>
                          {t('admin.transfers.open')}
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadTransferPdf(pdfData(row))}
                          aria-label={t('admin.transfers.pdfDownload')}
                          title={t('admin.transfers.pdfDownload')}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-background-300 text-foreground-600 hover:border-accent-400 hover:text-accent-700 transition-colors cursor-pointer"
                        >
                          <i className="ri-file-pdf-2-line text-[15px]" aria-hidden="true" />
                        </button>
                      </div>
                    </TableCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>

      <Modal
        open={composerOpen}
        title={t('admin.transfers.new')}
        subtitle={t('admin.transfers.newHint')}
        onClose={() => setComposerOpen(false)}
        width="max-w-2xl"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setComposerOpen(false)}>
              {t('admin.common.close')}
            </Button>
            <Button size="sm" loading={creating} onClick={submit} icon={<i className="ri-send-plane-line" aria-hidden="true" />}>
              {t('admin.transfers.submit')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3.5">
          {formError ? <Alert tone="error">{formError}</Alert> : null}
          {formNotice ? <Alert tone="success">{formNotice}</Alert> : null}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <Input
              label={t('admin.transfers.firstName')}
              name="firstName"
              value={form.firstName}
              onChange={(event) => setForm({ ...form, firstName: event.target.value })}
              icon="ri-user-line"
              placeholder="Amara"
            />
            <Input
              label={t('admin.transfers.lastName')}
              name="lastName"
              value={form.lastName}
              onChange={(event) => setForm({ ...form, lastName: event.target.value })}
              icon="ri-user-line"
              placeholder="Diallo"
            />
          </div>

          <Input
            label={t('admin.transfers.email')}
            name="email"
            type="email"
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
            icon="ri-mail-line"
            placeholder="beneficiaire@email.com"
            autoComplete="off"
          />

          <Input
            label={t('admin.transfers.iban')}
            name="iban"
            value={form.iban}
            onChange={(event) => setForm({ ...form, iban: event.target.value.toUpperCase() })}
            icon="ri-bank-card-line"
            placeholder="FR76 3000 4000 0312 3456 7890 143"
            spellCheck={false}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <Select
              label={t('admin.transfers.currency')}
              name="currency"
              value={form.currency}
              onChange={(event) => setForm({ ...form, currency: event.target.value })}
              options={CURRENCIES.map((code) => ({ value: code, label: code }))}
            />
            <div className="min-w-0">
              <Input
                label={t('admin.transfers.amount')}
                name="amount"
                type="number"
                min="0"
                max={MAX_TRANSFER_AMOUNT}
                step="0.01"
                value={form.amount}
                onChange={(event) => setForm({ ...form, amount: event.target.value })}
                icon="ri-money-euro-circle-line"
                placeholder="1250.00"
              />
              <p className="mt-1.5 text-[11.5px] text-foreground-500">
                {t('admin.transfers.amountHint', {
                  max: formatMoney(MAX_TRANSFER_AMOUNT, form.currency, i18n.language),
                })}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <Select
              label={t('admin.transfers.bank')}
              name="bankId"
              value={form.bankId}
              onChange={(event) => setForm({ ...form, bankId: event.target.value })}
              options={bankOptions}
              placeholder={t('admin.transfers.bankPh')}
              icon="ri-bank-line"
            />
            <Select
              label={t('admin.transfers.lang')}
              name="lang"
              value={form.lang}
              onChange={(event) => setForm({ ...form, lang: event.target.value })}
              options={MAIL_LANGS.map((code) => ({ value: code, label: MAIL_LANG_LABELS[code] }))}
            />
          </div>

          <Input
            label={t('admin.transfers.note')}
            name="note"
            value={form.note}
            onChange={(event) => setForm({ ...form, note: event.target.value })}
            icon="ri-file-text-line"
            placeholder={t('admin.transfers.notePh')}
          />

          <Alert tone="info">{t('admin.transfers.mailInfo')}</Alert>
        </div>
      </Modal>

      <Modal
        open={Boolean(selected)}
        title={t('admin.transfers.detail')}
        subtitle={selected?.reference}
        onClose={() => setSelected(null)}
        width="max-w-2xl"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
              {t('admin.common.close')}
            </Button>
            {selected ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openTransferPdf(pdfData(selected))}
                  icon={<i className="ri-file-pdf-2-line" aria-hidden="true" />}
                >
                  {t('admin.transfers.pdfOpen')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  loading={busy}
                  onClick={() => act(selected.linkActive ? 'link-off' : 'link-on')}
                  icon={
                    <i
                      className={selected.linkActive ? 'ri-link-unlink' : 'ri-link'}
                      aria-hidden="true"
                    />
                  }
                >
                  {selected.linkActive ? t('admin.transfers.linkDisable') : t('admin.transfers.linkEnable')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  loading={busy}
                  onClick={() => act('credentials')}
                  icon={<i className="ri-shield-keyhole-line" aria-hidden="true" />}
                >
                  {t('admin.transfers.sendCredentials')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  loading={busy}
                  onClick={() => act('resend')}
                  icon={<i className="ri-mail-send-line" aria-hidden="true" />}
                >
                  {t('admin.transfers.resend')}
                </Button>
                {['pending', 'verified'].includes(selected.status) ? (
                  <>
                    <Button variant="outline" size="sm" loading={busy} onClick={() => act('cancelled')}>
                      {t('admin.transfers.cancel')}
                    </Button>
                    <Button
                      variant="dark"
                      size="sm"
                      loading={busy}
                      disabled={!selected.verifiedAt}
                      onClick={() => act('executed')}
                    >
                      {t('admin.transfers.execute')}
                    </Button>
                  </>
                ) : null}
              </>
            ) : null}
          </>
        }
      >
        {selected ? (
          <div className="flex flex-col gap-4">
            {detailMessage ? <Alert tone="success">{detailMessage}</Alert> : null}
            {detailError ? <Alert tone="error">{detailError}</Alert> : null}

            <div className="flex items-center justify-between gap-3 rounded-xl border border-background-200 bg-background-100 px-4 py-3">
              <div>
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-foreground-500 font-label">
                  {t('admin.transfers.amount')}
                </p>
                <p className="mt-1 text-[20px] font-semibold text-foreground-950 tabular">
                  {formatMoney(selected.amount, selected.currency, i18n.language)}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <StatusBadge status={selected.status} />
                <EmailStateBadge status={selected.emailStatus} />
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-background-200 bg-background-100 px-4 py-3">
              <div className="min-w-0">
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-foreground-500 font-label">
                  {t('admin.transfers.activation')}
                </p>
                <div className="mt-1.5">
                  <AuthStateBadge transfer={selected} />
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-foreground-500 font-label">
                  {t('admin.transfers.verifiedAt')}
                </p>
                <p className="mt-1 text-[12.5px] font-semibold text-foreground-900">
                  {selected.verifiedAt
                    ? formatDate(selected.verifiedAt, i18n.language, true)
                    : t('admin.transfers.awaitingAuth')}
                </p>
              </div>
            </div>

            {!selected.verifiedAt && !['executed', 'cancelled'].includes(selected.status) ? (
              <Alert tone="info">{t('admin.transfers.notVerified')}</Alert>
            ) : null}

            <div className="rounded-xl border border-background-200 bg-background-100 p-3.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground-500 font-label">
                  {t('admin.transfers.activationLink')}
                </p>
                <LinkBadge active={selected.linkActive} />
              </div>
              <p className="mt-1 text-[11.5px] leading-relaxed text-foreground-500">
                {t('admin.transfers.activationHint')}
              </p>
              <div className="mt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <code className="flex-1 min-w-0 truncate rounded-lg border border-background-200 bg-background-50 px-2.5 py-2 text-[11px] text-foreground-700 font-label">
                  {activationLink(selected)}
                </code>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyActivationLink(selected)}
                    icon={<i className="ri-file-copy-line" aria-hidden="true" />}
                  >
                    {copied ? t('common.copied') : t('admin.transfers.copyLink')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => window.open(activationLink(selected), '_blank', 'noopener,noreferrer')}
                    icon={<i className="ri-external-link-line" aria-hidden="true" />}
                  >
                    {t('admin.transfers.openLink')}
                  </Button>
                </div>
              </div>
              {!selected.linkActive ? (
                <p className="mt-2 flex items-start gap-1.5 text-[11.5px] leading-relaxed text-accent-800">
                  <span className="flex items-center justify-center w-3.5 h-3.5 mt-0.5 shrink-0">
                    <i className="ri-error-warning-line text-[13px]" aria-hidden="true" />
                  </span>
                  {selected.linkDisabledAt
                    ? t('admin.transfers.linkDisabledAt', {
                        date: formatDate(selected.linkDisabledAt, i18n.language, true),
                      })
                    : t('admin.transfers.linkDisabledHint')}
                </p>
              ) : null}
            </div>

            <div className="rounded-xl border border-background-200 bg-background-100 p-3.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground-500 font-label">
                {t('admin.transfers.clientRecord')}
              </p>
              {selected.clientId ? (
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-foreground-950">{selected.clientName ?? '—'}</p>
                    <p className="mt-0.5 text-[11.5px] text-foreground-500 break-all">
                      {t('admin.transfers.clientLogin')} :{' '}
                      <span className="font-label font-semibold text-foreground-800">
                        {selected.clientLogin ?? '—'}
                      </span>
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-2.5 py-1 text-[11px] font-semibold text-primary-800 font-label whitespace-nowrap">
                    <span className="flex items-center justify-center w-3 h-3">
                      <i className="ri-check-line text-[11px]" aria-hidden="true" />
                    </span>
                    {t('admin.transfers.clientLinked')}
                  </span>
                </div>
              ) : (
                <p className="mt-2 text-[12px] text-foreground-500">{t('admin.transfers.noClient')}</p>
              )}
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              {[
                { label: t('admin.transfers.beneficiary'), value: selected.beneficiaryName },
                { label: t('admin.transfers.email'), value: selected.email },
                { label: t('admin.transfers.bank'), value: selected.bankName },
                { label: t('admin.transfers.iban'), value: selected.ibanMasked },
                { label: t('admin.transfers.date'), value: formatDate(selected.createdAt, i18n.language, true) },
                { label: t('admin.transfers.by'), value: selected.createdBy ?? '—' },
                { label: t('admin.transfers.note'), value: selected.note ?? '—' },
                { label: t('admin.transfers.emailStatus'), value: selected.emailStatus },
                {
                  label: t('admin.transfers.lang'),
                  value: MAIL_LANG_LABELS[String(selected.lang ?? 'fr')] ?? 'Français',
                },
                { label: t('admin.transfers.authAttempts'), value: String(selected.authAttempts ?? 0) },
              ].map((item) => (
                <div key={item.label} className="min-w-0">
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground-500 font-label">
                    {item.label}
                  </dt>
                  <dd className="mt-0.5 text-[13px] text-foreground-900 break-words">{item.value}</dd>
                </div>
              ))}
            </dl>

            {selected.emailError ? (
              <Alert tone="error" title={t('admin.transfers.emailError')}>
                <span className="break-all font-label text-[11.5px]">{selected.emailError}</span>
              </Alert>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}